import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import { useCoachReadiness } from "@/hooks/useCoachReadiness";
import { useAuth } from "@/hooks/useAuth";

type Program = {
  id: string;
  title: string;
  program_url: string | null;
  focus_areas: string[];
  award_min: number | null;
  award_max: number | null;
  cycle: string | null;
  description: string | null;
  funders: { name: string; slug: string; funder_type: string } | null;
};

const RecommendedForYou = () => {
  const { user } = useAuth();
  const { hasAssessment, focusAreas, loading } = useCoachReadiness();

  const { data: programs, isLoading } = useQuery({
    enabled: hasAssessment && focusAreas.length > 0,
    queryKey: ["recommended-programs", focusAreas.join(",")],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("grant_programs")
        .select("id, title, program_url, focus_areas, award_min, award_max, cycle, description, funders:funder_id(name, slug, funder_type)")
        .eq("verified", true)
        .overlaps("focus_areas", focusAreas)
        .order("title")
        .limit(6);
      if (error) throw error;
      return (data ?? []) as unknown as Program[];
    },
  });

  if (loading) return null;

  if (!user) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center space-y-3">
        <Sparkles className="h-5 w-5 text-primary mx-auto" />
        <h3 className="font-display text-lg font-semibold">Sign in for Coach-recommended funding</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Take a quick readiness assessment and we'll highlight verified programs aligned to your work.
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button asChild size="sm" variant="outline"><Link to="/auth">Sign in</Link></Button>
          <Button asChild size="sm"><Link to="/coach">Start an assessment</Link></Button>
        </div>
      </div>
    );
  }

  if (!hasAssessment || focusAreas.length === 0) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center space-y-3">
        <Sparkles className="h-5 w-5 text-primary mx-auto" />
        <h3 className="font-display text-lg font-semibold">Get personalized funding matches</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Complete a short readiness assessment with the Coach and we'll surface verified programs that fit your focus areas.
        </p>
        <Button asChild size="sm"><Link to="/coach">Get a readiness assessment</Link></Button>
      </div>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Finding matches…</p>;
  }

  if (!programs || programs.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center space-y-3">
        <p className="text-sm text-muted-foreground">
          No verified programs match your focus areas right now. Browse the full directory below or ask the Coach for tailored guidance.
        </p>
        <Button asChild size="sm" variant="outline"><Link to="/coach">Ask the Coach</Link></Button>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {programs.map((p) => (
        <div key={p.id} className="border border-primary/30 rounded-lg p-4 bg-card hover:border-primary/60 transition">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="font-semibold text-sm leading-tight">{p.title}</h4>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Badge variant="outline" className="gap-1 text-[10px] bg-primary/10 text-primary border-primary/30">
                <Sparkles className="h-3 w-3" /> Coach match
              </Badge>
              <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                <ShieldCheck className="h-3 w-3" /> Verified
              </Badge>
            </div>
          </div>
          {p.funders && (
            <p className="text-xs text-muted-foreground mb-2">
              <Link to={`/funders/${p.funders.slug}`} className="hover:text-primary hover:underline">
                {p.funders.name}
              </Link>{" "}
              · {p.funders.funder_type}
            </p>
          )}
          {p.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-3">{p.description}</p>}
          <div className="flex flex-wrap gap-2 text-[10px] mb-3">
            {p.cycle && <Badge variant="outline">{p.cycle}</Badge>}
            {(p.award_min || p.award_max) && (
              <Badge variant="outline">
                ${(p.award_min ?? 0).toLocaleString()} – ${(p.award_max ?? p.award_min ?? 0).toLocaleString()}
              </Badge>
            )}
            {p.focus_areas?.slice(0, 2).map((f) => (
              <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>
            ))}
          </div>
          {p.program_url && (
            <a href={p.program_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              View program <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

export default RecommendedForYou;